import {
  Badge,
  Button,
  ButtonGroup,
  Icon,
  Select,
  SelectItem,
  Tooltip,
} from "@prismicio/editor-ui"

import { getFieldIcon } from "./LinkField"

export type FieldSelectType = "web" | "document" | "media" | "any"

interface Props {
  hasLink: boolean
  onUnlink: () => void
  selectedType?: FieldSelectType
  selectEnabled: boolean
  value: string
  onOpenLinkModal: () => void
  onValueChange: (newValue: FieldSelectType) => void
  openInNewTab?: boolean
  condensedButtons?: boolean
  showNewTabButton?: boolean
  displayTextEnabled: boolean
  variants: string[]
  variant?: string
  onVariantChange: (newVariant: string) => void
}

export function LinkFieldActions(props: Props) {
  const {
    hasLink,
    onUnlink,
    selectEnabled,
    selectedType,
    value,
    onOpenLinkModal,
    onValueChange,
    openInNewTab = true,
    condensedButtons = false,
    showNewTabButton = false,
    displayTextEnabled,
    variants,
    variant,
    onVariantChange,
  } = props

  return (
    <>
      {!displayTextEnabled && showNewTabButton && openInNewTab && (
        <Badge
          title="Open in a new tab"
          color="indigo"
          size="small"
          icon="openInNew"
          onClick={onOpenLinkModal}
        />
      )}

      {!hasLink && selectEnabled && (
        <Select
          // The key is needed to reset the Select when the user unlinks the field
          // See this GitHub issue for more information: https://github.com/radix-ui/primitives/issues/1569
          key={selectedType === undefined ? "unlinked" : "linked"}
          value={selectedType === "any" ? undefined : selectedType}
          color="grey"
          size="small"
          boldTriggerText
          position="popper"
          sideOffset={4}
          startIcon={getFieldIcon({ selectType: selectedType })}
          disabled={!selectEnabled}
          onValueChange={onValueChange}
          placeholder={condensedButtons ? "" : getContent("any")}
          onlyStartIcon={condensedButtons && selectedType !== undefined}
          renderTrigger={({ button }) => (
            <Tooltip
              content={getContent(selectedType)}
              visible={condensedButtons}
            >
              {button}
            </Tooltip>
          )}
        >
          <SelectItem value="web" icon="public">
            {getContent("web")}
          </SelectItem>
          <SelectItem value="document" icon="description">
            {getContent("document")}
          </SelectItem>
          <SelectItem value="media" icon="image">
            {getContent("media")}
          </SelectItem>
        </Select>
      )}

      {!hasLink && !selectEnabled && (
        <Button
          startIcon={getFieldIcon({ selectType: selectedType })}
          color="grey"
          textColor="link"
          textWeight="normal"
          size="small"
          onClick={onOpenLinkModal}
        >
          {getContent(selectedType)}
        </Button>
      )}

      {hasLink && !displayTextEnabled && (
        <Button
          startIcon="close"
          color="grey"
          size="small"
          onClick={onUnlink}
          aria-label="Close"
        />
      )}

      {hasLink && displayTextEnabled && (
        <ButtonGroup density="compact">
          <Button
            startIcon={getFieldIcon({ selectType: selectedType })}
            renderEndIcon={() =>
              openInNewTab && selectedType === "web" ? (
                <Icon name="openInNew" size="extraSmall" color="indigo11" />
              ) : null
            }
            color="grey"
            textColor="link"
            textWeight="normal"
            size="small"
            disabled={!hasLink}
            onClick={onOpenLinkModal}
          >
            {value.length > 30 ? `${value.slice(0, 30)}...` : value}
          </Button>
          <Button
            startIcon="close"
            color="grey"
            size="small"
            onClick={onUnlink}
          />
        </ButtonGroup>
      )}

      {variants.length > 1 && (
        <Select
          value={variant}
          color="grey"
          size="small"
          boldTriggerText
          position="popper"
          sideOffset={4}
          startIcon="colorLens"
          onValueChange={onVariantChange}
        >
          {variants.map((variantOption, index) => (
            <SelectItem key={`${variantOption}-${index}`} value={variantOption}>
              {variantOption}
            </SelectItem>
          ))}
        </Select>
      )}
    </>
  )
}

function getContent(type?: FieldSelectType) {
  if (!type || type === "any") return "Choose link type"

  switch (type) {
    case "document":
      return "Link to Prismic page"
    case "web":
      return "Link to web page"
    case "media":
      return "Link to media"
  }
}
